import { newTempId } from './navigation-tree.utils';

import type { FlatDraftItem } from './navigation-tree.utils';

export interface FooterSectionGroup {
  section: FlatDraftItem;
  items: FlatDraftItem[];
}

/** Split a flat draft list into footer sections (depth 0) and their links (depth 1). */
export const flatToFooterSections = (items: FlatDraftItem[]): FooterSectionGroup[] => {
  const groups: FooterSectionGroup[] = [];
  let current: FooterSectionGroup | null = null;

  for (const item of items.filter((i) => !i.isDeleted)) {
    if (item.depth === 0) {
      current = { section: item, items: [] };
      groups.push(current);
    } else if (current) {
      current.items.push({ ...item, depth: 1 });
    }
  }

  return groups;
};

/** Rebuild the flat draft list from section groups, preserving soft-deleted rows. */
export const footerSectionsToFlat = (
  groups: FooterSectionGroup[],
  deleted: FlatDraftItem[],
): FlatDraftItem[] => {
  const active: FlatDraftItem[] = [];
  for (const group of groups) {
    active.push({ ...group.section, depth: 0, href: null });
    for (const item of group.items) {
      active.push({ ...item, depth: 1 });
    }
  }
  return [...active, ...deleted];
};

export const createFooterSection = (): FlatDraftItem => ({
  id: newTempId(),
  label: 'New Section',
  href: null,
  pageId: null,
  openInNewTab: false,
  isVisible: true,
  depth: 0,
  itemType: 'custom',
  isNew: true,
});

export const createFooterLink = (): FlatDraftItem => ({
  id: newTempId(),
  label: 'New link',
  href: '/',
  pageId: null,
  openInNewTab: false,
  isVisible: true,
  depth: 1,
  itemType: 'custom',
  isNew: true,
});

/** Insert new footer links after the chosen section's existing items. */
export const insertFooterLinks = (
  items: FlatDraftItem[],
  sectionId: string,
  links: FlatDraftItem[],
): FlatDraftItem[] => {
  const groups = flatToFooterSections(items);
  const deleted = items.filter((i) => i.isDeleted);
  const next = groups.map((g) =>
    g.section.id === sectionId
      ? { ...g, items: [...g.items, ...links.map((l) => ({ ...l, depth: 1 }))] }
      : g,
  );
  return footerSectionsToFlat(next, deleted);
};

export const lastFooterSectionId = (items: FlatDraftItem[]): string | null => {
  const groups = flatToFooterSections(items);
  return groups.length > 0 ? groups[groups.length - 1]!.section.id : null;
};
